import { Box, VisuallyHidden } from '@leagueplatform/genesis-core';

// The design specifies a 10px dot. Genesis sets a 10px root font size, so 1rem
// is an exact match and stays proportional if the root size ever changes.
const INDICATOR_SIZE = '1rem';

// The full-width nav entry pads its label by `$half` on each side. Offsetting the
// dot by that padding less its own width puts the dot's leading edge on the
// label's trailing edge, which is where the design places it.
const ENTRY_INLINE_PADDING = '$half';

// The label is concatenated onto the entry's own text to form one accessible
// name, and neither Blink nor WebKit inserts a boundary between inline children,
// so the separator has to be part of the label. A plain space would not survive:
// `VisuallyHidden` is out of flow, so its text starts a line, and a collapsible
// space at the start of a line is removed. A no-break space is not collapsible.
const NAME_SEPARATOR = '\u00a0';

/**
 * How the dot attaches to the nav entry it sits in. The design draws the same
 * dot in both navs — a superscript on the label's trailing edge — but the two
 * entries have different boxes, so it is reached two ways:
 *
 * - `overlay` for the horizontal nav, whose entry hugs its label, so the dot can
 *   be positioned against the entry's trailing edge and stay out of the layout.
 * - `afterLabel` for the collapsed menu, whose entry spans the drawer's full
 *   width. Its trailing edge is nowhere near the label, so the dot follows the
 *   label in flow instead and is raised out of the centred row.
 */
type NavIndicatorPlacement = 'overlay' | 'afterLabel';

const placementStyles = {
  overlay: {
    position: 'absolute',
    // The entry centres its label vertically, so the dot is lifted from the
    // midpoint by its own height plus a small rise to clear the label.
    top: '50%',
    marginTop: `calc(-${INDICATOR_SIZE} - 0.1rem)`,
    insetInlineEnd: `calc(${ENTRY_INLINE_PADDING} - ${INDICATOR_SIZE})`,
  },
  afterLabel: {
    // Relative, so raising the dot to the label's top does not disturb the row.
    position: 'relative',
    top: '-0.6rem',
    // The row is a flex line sized by its icon; without this the dot is a
    // shrinkable item and a long label squashes it out of round.
    flexShrink: 0,
  },
} as const;

type NavIndicatorProps = {
  /**
   * Text announcing what the indicator signals. Rendered visually hidden inside
   * the nav entry, so it becomes part of that entry's accessible name, after a
   * separator so it does not run into the entry's own text.
   */
  label: string;
  /** Defaults to the horizontal nav's treatment. */
  placement?: NavIndicatorPlacement;
};

/**
 * A dot marking a navigation entry as needing attention.
 *
 * The dot carries no text, so on its own it announces nothing — the meaning
 * comes from {@link NavIndicatorProps.label}, hidden visually but left in the
 * accessibility tree. Rendering it inside the nav entry makes it part of that
 * entry's accessible name ("Support, you have unread messages") rather than a
 * separate live region, which would announce out of context.
 */
export const NavIndicator = ({
  label,
  placement = 'overlay',
}: NavIndicatorProps) => (
  <Box
    as="span"
    data-testid="nav-indicator"
    css={{
      ...placementStyles[placement],
      width: INDICATOR_SIZE,
      height: INDICATOR_SIZE,
      borderRadius: '$circle',
      backgroundColor: '$criticalIcon',
      // Forced-colors mode replaces background colours with the user's own
      // palette, which would erase a dot drawn only with a background. A border
      // in the same colour is invisible normally and gets recoloured there,
      // leaving the dot visible as a ring. The Genesis reset sets
      // `box-sizing: border-box`, so this does not grow the dot.
      borderWidth: '$thin',
      borderStyle: 'solid',
      borderColor: '$criticalIcon',
    }}
  >
    <VisuallyHidden>{`${NAME_SEPARATOR}${label}`}</VisuallyHidden>
  </Box>
);
